/**
 * Pure editing rules for a loadout draft in the hangar (Phase 6). The server validates again on
 * save; these only keep the editor honest as the pilot clicks (slot counts, exclusive module
 * groups, one part per item) so the "Save" that follows is normally accepted.
 */
import { COSMETICS, getWeapon, MODULES, type MechDefinition } from '@steelwake/shared/data';
import { defaultLoadout, type Loadout } from '@steelwake/shared/loadout';
import type { Hangar as HangarView } from '@steelwake/shared/progression';

export type PaintChannel = 'primary' | 'secondary' | 'accent';
export type ColourChannel = PaintChannel | 'emissive' | 'decal';

/** One shared default so selectors stay referentially stable between renders. */
const STOCK: Loadout = defaultLoadout();

/** The saved loadout for a mech, or the defaults when none was saved yet. */
export function draftFor(view: HangarView | null, mechId: string): Loadout {
  return view?.loadouts[mechId] ?? STOCK;
}

/** Equips or unequips a module; equipping replaces a module of the same group. */
export function toggleModule(loadout: Loadout, id: string, def: MechDefinition): Loadout {
  const module = MODULES[id];
  if (!module) return loadout;
  if (loadout.modules.includes(id)) {
    return { ...loadout, modules: loadout.modules.filter((m) => m !== id) };
  }
  const kept = loadout.modules.filter((m) => MODULES[m]?.group !== module.group);
  if (kept.length >= def.slots.module) return loadout;
  return { ...loadout, modules: [...kept, id] };
}

export function setChannel(loadout: Loadout, channel: ColourChannel, id: string | null): Loadout {
  return { ...loadout, cosmetics: { ...loadout.cosmetics, [channel]: id } };
}

/** Equips or unequips a premium part, within the mech's cosmetic slots. */
export function togglePart(loadout: Loadout, id: string, def: MechDefinition): Loadout {
  const item = COSMETICS[id];
  if (!item || item.kind !== 'part') return loadout;
  const parts = loadout.cosmetics.parts;
  if (parts.includes(id)) {
    return {
      ...loadout,
      cosmetics: { ...loadout.cosmetics, parts: parts.filter((p) => p !== id) },
    };
  }
  if (parts.length >= def.slots.cosmetic) return loadout;
  return { ...loadout, cosmetics: { ...loadout.cosmetics, parts: [...parts, id] } };
}

export function sameLoadout(a: Loadout, b: Loadout): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const MOD_LABELS: Record<string, [string, boolean]> = {
  speedScale: ['speed', true],
  turnScale: ['turn rate', true],
  fireRateScale: ['fire rate', true],
  heatScale: ['heat per shot', false],
  dissipationScale: ['heat dissipation', true],
  spreadScale: ['spread', false],
  cooldownScale: ['cooldowns', false],
  jetThrustScale: ['jet thrust', true],
  jetCostScale: ['jet cost', false],
};

/** One line per stat the equipped modules change, for the loadout tab. */
/** Picks the stock primary (null) or one of the mech's alternates for the draft. */
export function choosePrimary(loadout: Loadout, id: string | null, def: MechDefinition): Loadout {
  const primary = id !== null && def.alternates.primary.includes(id) ? id : null;
  return { ...loadout, weapons: { primary } };
}

/** A one-line stat card for a weapon choice. */
export function weaponSummary(id: string): string {
  const w = getWeapon(id);
  const rate = w.fireRate >= 1 ? `${String(w.fireRate)}/s` : `${(1 / w.fireRate).toFixed(1)} s`;
  return `${w.name} · ${w.kind} · ${String(w.damage)} dmg · ${rate}`;
}

export function moduleSummary(ids: readonly string[]): string[] {
  const lines: string[] = [];
  for (const id of ids) {
    const module = MODULES[id];
    if (!module) continue;
    for (const [key, value] of Object.entries(module.mods)) {
      if (key === 'armorBonus') {
        lines.push(`+${String(Math.round(value * 100))} % armour`);
        continue;
      }
      const label = MOD_LABELS[key];
      if (!label) continue;
      const pct = Math.round((value - 1) * 100);
      lines.push(`${pct >= 0 ? '+' : ''}${String(pct)} % ${label[0]}`);
    }
  }
  return lines;
}
